import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { translateSafe } from '@/utils/translations';

/** UI operators for standard column filters */
export type FilterOperator = 'equals' | 'is_not' | 'contains';

export const FILTER_OPERATORS: FilterOperator[] = ['equals', 'is_not', 'contains'];

/** Taxonomy classification-rule filter pickers: equals only (negation via customized NOT). */
export const TAXONOMY_FILTER_OPERATORS: FilterOperator[] = ['equals'];

export function getEffectiveAllowedOperators(allowedOperators?: FilterOperator[]): FilterOperator[] {
  return allowedOperators ?? FILTER_OPERATORS;
}

export function hasSingleOperatorChoice(allowedOperators?: FilterOperator[]): boolean {
  return getEffectiveAllowedOperators(allowedOperators).length <= 1;
}

export function getOperatorLabel(operator: FilterOperator): string {
  switch (operator) {
    case 'equals':
      return translateSafe('operation', 'eq');
    case 'is_not':
      return translateSafe('operation', 'isNot');
    case 'contains':
      return translateSafe('operation', 'contains');
    default:
      return operator;
  }
}

/** Detect operator from persisted filter shape */
export function isFilterExcluded(filter: GenericFilter): boolean {
  return filter.is_exclude ?? (filter as GenericFilter & { is_excluded?: boolean }).is_excluded ?? false;
}

export function filterToOperator(filter: GenericFilter): FilterOperator {
  if (isContainsFilter(filter)) {
    return 'contains';
  }
  return isFilterExcluded(filter) ? 'is_not' : 'equals';
}

export function isContainsFilter(filter: GenericFilter): boolean {
  const search = filter.search?.trim();
  if (!search) return false;
  if (filter.select_all) return false;
  // Contains uses search as the match term with empty or single placeholder values
  return (filter.values?.length ?? 0) === 0;
}

export function supportsOperatorSelection(column: string): boolean {
  const specialColumns = [
    'first_interaction',
    'last_interaction',
    'users_interactions',
    'user_conversations_count',
    'user_conversation_length',
    'user_interaction_position',
    'last_interaction_tag',
    'active_periods',
    'avg_session_length',
  ];
  return !specialColumns.includes(column);
}

export type BuildFilterParams = {
  column: string;
  operator: FilterOperator;
  values: string[];
  selectAll?: boolean;
  containsText?: string;
};

export function buildGenericFilter({
  column,
  operator,
  values,
  selectAll,
  containsText,
}: BuildFilterParams): GenericFilter {
  if (operator === 'contains') {
    const text = (containsText ?? values[0] ?? '').trim();
    return {
      column,
      values: [],
      search: text,
      is_exclude: false,
    };
  }

  return {
    column,
    values,
    is_exclude: operator === 'is_not',
    select_all: selectAll,
    search: undefined,
  };
}

/** Display text for chip value segment */
export function getFilterValueDisplayText(filter: GenericFilter, formattedValues: string): string {
  if (isContainsFilter(filter)) {
    return filter.search?.trim() ?? '';
  }
  if (filter.select_all && formattedValues) {
    return `${formattedValues} + ALL`;
  }
  if (filter.select_all) {
    return 'ALL';
  }
  return formattedValues;
}
